import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Discrepancy } from "../api/types";
import { flagTokens, markGist, markLabel, type ScriptMark, type ScriptSnippet } from "../lib/scriptNotes";
import { Icon } from "./icons";

/**
 * A flag that meets a mark the script makes - a typed note beside its words, a
 * Word comment on them, struck words where they sat, a highlight, a circle or a
 * handwritten note - shows it on its card: one line in the list, and, when the
 * flag is selected, the note's words and the page itself with the flagged words
 * ringed. "Intended, per the script" clears the flag without making it a pickup.
 */

const typed = (m: ScriptMark) => m.kind === "note" || m.kind === "comment" || m.kind === "struck";
// A note's own words, in quotes - unless it brings its own.
const quoted = (t: string) => (/[“”"]/.test(t) ? t : `“${t}”`);

function MarkIcon({ m }: { m: ScriptMark }) {
  return m.kind === "comment"
    ? <Icon name="message" size={14} className="sn-icon sn-icon-grey" />
    : <Icon name="pen-line" size={14} className="sn-icon" />;
}

/** The one line a flag's card carries in the list. */
export function ScriptNoteLine({ marks, onOpen }: { marks: ScriptMark[]; onOpen: () => void }) {
  const lead = marks[0];
  const gist = markGist(lead);
  return (
    <button
      className={`sn-line${lead.kind === "comment" ? " sn-grey" : ""}`}
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      title={`${markLabel(lead)} on p.${lead.page}`}
    >
      <MarkIcon m={lead} />
      <span className="sn-label">{markLabel(lead)}</span>
      {lead.kind !== "comment" && <span className="sn-dot" style={{ background: lead.color }} />}
      <span className={`sn-gist${typed(lead) ? " sn-said" : ""}`}>{typed(lead) ? quoted(gist) : gist}</span>
      {marks.length > 1 && <span className="sn-more">+{marks.length - 1}</span>}
      <span className="sn-show">Show page</span>
      <Icon name="chevron-down" size={14} />
    </button>
  );
}

/** The card opened: what the script says, the page, and what to do about it. */
export function ScriptNotePanel({
  projectId, chapterId, d, marks, cleared, onIntended, onHide,
}: {
  projectId: string;
  chapterId: string;
  d: Pick<Discrepancy, "manuscript_token_indices" | "insert_before_index">;
  marks: ScriptMark[];
  cleared: boolean;
  onIntended: () => void;
  onHide: () => void;
}) {
  const tokens = flagTokens(d).join(",");
  const { data: snip, isError } = useQuery({
    queryKey: ["script-snippet", projectId, chapterId, tokens],
    queryFn: () => api<ScriptSnippet>(
      `/api/modules/proofing/projects/${projectId}/chapters/${chapterId}/script-notes/snippet?tokens=${tokens}`),
    staleTime: Infinity,
    retry: false,
  });
  const lead = marks[0];
  const said = marks.filter(typed).slice(0, 3);
  const page = snip?.page ?? lead.page;
  const pdfPage = snip?.pdf_page ?? lead.pdf_page;

  return (
    <div className="sn-panel" onClick={(e) => e.stopPropagation()}>
      <div className="sn-head">
        <MarkIcon m={lead} />
        <span className="sn-label">{markLabel(lead)}</span>
        <span className="sn-meta">· p.{page}{said.length ? "" : ` · ${markGist(lead)}`}</span>
        <span className="sn-spacer" />
        <a className="sn-open" href={`/api/projects/${projectId}/manuscript.pdf#page=${pdfPage}`}
          target="_blank" rel="noreferrer">Open the PDF page</a>
        <button className="sn-hide" aria-label="Hide the script's note" title="Hide" onClick={onHide}>
          <Icon name="chevron-up" size={15} />
        </button>
      </div>

      {said.map((m) =>
        m.kind === "comment" ? (
          <div key={m.id} className="sn-thread">
            {(m.parts?.length ? m.parts : [{ who: "", text: m.text || "" }]).map((p, i) => (
              <div key={i} className="sn-comment">{p.who && <b>{p.who}</b>} {p.text}</div>
            ))}
          </div>
        ) : (
          <div key={m.id} className="sn-quote">
            <span className="sn-dot" style={{ background: m.color }} />
            {m.kind === "struck" ? <span><s>{m.text}</s> <span className="sn-meta">struck out</span></span> : quoted(m.text || "")}
          </div>
        ),
      )}

      <div className="sn-snip" style={{ aspectRatio: String(snip?.aspect ?? 4.6) }}>
        {snip ? (
          <>
            <img src={snip.image} alt="The script page around these words, with its marks" />
            {snip.rings.map((r, i) => (
              <span key={i} className="sn-ring" style={{
                left: `${r[0] * 100}%`, top: `${r[1] * 100}%`, width: `${r[2] * 100}%`, height: `${r[3] * 100}%`,
              }} />
            ))}
          </>
        ) : (
          <div className="sn-wait">{isError ? "Couldn't show the page here — open the PDF page instead." : "Fetching the page…"}</div>
        )}
      </div>

      <div className="sn-actions">
        {cleared ? (
          <>
            <span className="sn-cleared"><Icon name="check" size={13} /> Cleared: intended, per the script</span>
            <button className="btn sm ghost" onClick={onIntended}>Undo</button>
          </>
        ) : (
          <>
            <button className="btn sm sn-intended" onClick={onIntended}
              title="Clear this flag without making it a pickup: the narrator read what the script asked for">
              <Icon name="check" size={14} /> Intended, per the script
            </button>
            <button className="btn sm ghost" onClick={onHide}>Keep the flag</button>
          </>
        )}
        <span className="sn-spacer" />
        {snip && <span className="sn-hint">Ringed: the words this flag is about</span>}
      </div>
    </div>
  );
}
